import { useState } from 'react';
import styles from './result-card.module.scss';
import ImageSVG from 'assets/icons/image.svg';
import { Link } from 'src/navigation';
import RedundantLink from 'components/accesibility/redundant-link';
import ExhibitionDisplayDates from 'components/exhibitions/display-dates';
import LegacyOrMarkdownParser from 'components/rich-text/legacy-or-markdown-parser';
import { JsonView, allExpanded } from 'react-json-view-lite';

import 'react-json-view-lite/dist/index.css';

export const ResultCard = ({
	result,
	loading,
	lazyLoad = true,
	onResultClick,
	showHighlights = false,
}: IResultCardProps) => {
	const [showImageFallback, setShowImageFallback] = useState(!result?.image);

	const handleImageError = (e) => {
		console.error(e);
		setShowImageFallback(true);
	};

	const bestBetLabel = result.bestBet ? (
		<span className={styles.bestBetTitle}>Suggested Result</span>
	) : (
		''
	);

	//Teaser Text is useful for all cards except "Artwork" where its redundant with dataFields.
	const teaserText = (
		<div className={styles.resultTeaser}>
			<LegacyOrMarkdownParser>{result.teaser}</LegacyOrMarkdownParser>
		</div>
	);

	const dataFieldText = (
		<div className={styles.dataContainer}>
			{result.dataFields.map((data) => {
				return (
					!!data.value && (
						<div className={styles.data} key={`data-${data.name}`}>
							<span className={styles.dataTitle}>{data.name}:</span>
							<span className={styles.dataValue}>
								<LegacyOrMarkdownParser>{data.value}</LegacyOrMarkdownParser>
							</span>
						</div>
					)
				);
			})}
		</div>
	);

	const classString = () =>
		[
			styles.resultCard,
			'result-card',
			styles[result.buttonText],
			result.status === 'Past Exhibition' && styles.pastExhibition,
			result.bestBet && styles.bestBet,
			loading && styles.loading,
		]
			.filter(Boolean)
			.join(' ');

	return (
		<div className={classString()}>
			<div className={styles.imageWrapper}>
				{result?.url && (
					<RedundantLink href={result.url} onClick={onResultClick} />
				)}
				{showImageFallback || loading ? (
					<ImageSVG className={styles.imageFallBack} />
				) : (
					/* eslint-disable @next/next/no-img-element -- TODO: I wish we could optimize these if it's a Sanity Image 🤔 */
					<img
						src={result.image}
						alt={result.imageAlt || `Image for ${result.title}`} // TODO: this fallback is fake accessibility - should pull a meaingful alt if it's not role=presentation
						loading={lazyLoad ? 'lazy' : 'eager'}
						onError={handleImageError}
						className={styles.image}
					/>
					/* eslint-enable @next/next/no-img-element */
				)}
			</div>
			<div className={styles.body}>
				<div className={styles.titles}>
					<div className={styles.descriptor}>
						{bestBetLabel} {result.status ? result.status : result.buttonText}
					</div>
					{result?.url ? (
						// some results, like the roof garden "suggested result" promo, do not have a URL, somehow
						<Link
							href={result.url}
							className={styles.link}
							onClick={onResultClick}
						>
							<h2 className={styles.resultTitle}>
								<LegacyOrMarkdownParser>{result.title}</LegacyOrMarkdownParser>
							</h2>
						</Link>
					) : (
						<h2 className={styles.resultTitle}>
							<LegacyOrMarkdownParser>{result.title}</LegacyOrMarkdownParser>
						</h2>
					)}

					{result.dates &&
						(result.cardType === 'Exhibition' &&
						typeof result.dates === 'object' ? (
							<div className={styles.subTitle}>
								<ExhibitionDisplayDates exhibitionDates={result.dates} />
							</div>
						) : (
							<div className={styles.subTitle}>
								<LegacyOrMarkdownParser>
									{result.dates as string}
								</LegacyOrMarkdownParser>
							</div>
						))}
					{result.subTitle && (
						<p className={styles.subTitle}>{result.subTitle}</p>
					)}
				</div>
				{result.dataFields.length > 0 &&
					result.cardType != 'event' &&
					dataFieldText}
				{teaserText}
				{/* This is why we can't have nice things. */}
				{result.dataFields.length > 0 &&
					result.cardType == 'event' &&
					dataFieldText}

				{showHighlights && result?.highlight && (
					<JsonView data={result.highlight} shouldExpandNode={allExpanded} />
				)}
			</div>
		</div>
	);
};

interface IResultCardProps {
	result: {
		bestBet?: boolean;
		buttonText?: string;
		cardType?: string;
		dataFields?: Array<{
			name: string;
			value: string;
		}>;
		dates?: string | Record<string, unknown>;
		highlight?: Record<string, unknown>;
		image?: string;
		imageAlt?: string;
		status?: string;
		subTitle?: string;
		teaser?: string;
		title?: string;
		url?: string;
	};
	lazyLoad?: boolean;
	loading?: boolean;
	onResultClick?: () => void;
	showHighlights?: boolean;
}
